import { Navigate, Link } from "react-router-dom";
import {
    Phone, MapPin, ArrowRight, CheckCircle2, Star, Award, Users, Clock,
    Video, GraduationCap, ShieldCheck, MessageCircle,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import RegistrationForm from "../components/RegistrationForm";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import { getBoardSubjectCityData, PROG_BOARDS, PROG_SUBJECTS } from "../data/programmaticSEO";
import { SOCIAL } from "../data/siteData";

const ENQUIRY_PHONE = "+91 7000530316";
const ENQUIRY_PHONE_RAW = "+917000530316";

export default function BoardSubjectCity({ boardSlug, subjectSlug, citySlug }) {
    const data = getBoardSubjectCityData(boardSlug, subjectSlug, citySlug);
    if (!data) return <Navigate to="/" replace />;

    const orgSchema = {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        "@id": `${data.canonical}#organization`,
        "name": "Anand Tutorials",
        "url": "https://anandtutorials.com",
        "logo": "https://anandtutorials.com/logo-anand-tutorials.png",
        "sameAs": [SOCIAL.facebook, SOCIAL.instagram].filter(Boolean),
    };

    const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${data.canonical}#service`,
        "serviceType": `${data.board.label} ${data.subject.label} Online Tuition in ${data.city.name}`,
        "provider": { "@id": `${data.canonical}#organization` },
        "areaServed": {
            "@type": "City",
            "name": data.city.name,
            "containedInPlace": { "@type": "AdministrativeArea", "name": data.city.state },
        },
        "audience": { "@type": "EducationalAudience", "educationalRole": "student" },
        "category": "Online Educational Services",
        "url": data.canonical,
        "description": data.intro,
    };

    const courseSchema = {
        "@context": "https://schema.org",
        "@type": "Course",
        "@id": `${data.canonical}#course`,
        "name": `${data.board.label} ${data.subject.label} — 1:1 Live Online Tuition (${data.city.name})`,
        "description": data.seoDescription,
        "url": data.canonical,
        "provider": { "@id": `${data.canonical}#organization`, "name": "Anand Tutorials", "sameAs": "https://anandtutorials.com" },
        "audience": {
            "@type": "EducationalAudience",
            "educationalRole": "student",
            "geographicArea": { "@type": "City", "name": data.city.name },
        },
        "hasCourseInstance": [{
            "@type": "CourseInstance",
            "courseMode": "online",
            "inLanguage": "en",
            "name": `${data.board.label} ${data.subject.label} · ${data.city.name}`,
            "location": { "@type": "VirtualLocation", "url": data.canonical },
            "offers": [{
                "@type": "Offer",
                "availability": "https://schema.org/InStock",
                "price": "0",
                "priceCurrency": "INR",
                "category": "Free demo class",
                "url": "https://anandtutorials.com/contact",
            }],
        }],
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": "850",
            "bestRating": "5",
            "worstRating": "1",
        },
    };

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": `${data.canonical}#faq`,
        "mainEntity": data.faqs.map(([q, a]) => ({
            "@type": "Question",
            "name": q,
            "acceptedAnswer": { "@type": "Answer", "text": a },
        })),
    };

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${data.canonical}#breadcrumb`,
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://anandtutorials.com/" },
            { "@type": "ListItem", "position": 2, "name": "1:1 Online Classes", "item": "https://anandtutorials.com/online-classes" },
            { "@type": "ListItem", "position": 3, "name": data.city.name, "item": `https://anandtutorials.com/home-tuition-${data.citySlug}` },
            { "@type": "ListItem", "position": 4, "name": `${data.board.label} ${data.subject.label} Tutor`, "item": data.canonical },
        ],
    };

    // Related programmatic links (same city, other boards + subjects) for internal linking
    const relatedBoards = Object.keys(PROG_BOARDS).filter((b) => b !== boardSlug).slice(0, 5);
    const relatedSubjects = Object.keys(PROG_SUBJECTS).filter((s) => s !== subjectSlug).slice(0, 5);

    return (
        <div data-testid={`prog-${boardSlug}-${subjectSlug}-${citySlug}`}>
            <SEO
                title={data.seoTitle}
                description={data.seoDescription}
                keywords={data.keywords}
                path={data.path}
                canonicalPath={data.path}
                schemas={[orgSchema, serviceSchema, courseSchema, faqSchema, breadcrumbSchema]}
            />

            <Breadcrumbs items={[
                { name: "1:1 Online Classes", to: "/online-classes" },
                { name: data.city.name, to: `/home-tuition-${data.citySlug}` },
                { name: `${data.board.label} ${data.subject.label} Tutor` },
            ]} />

            <PageHeader
                eyebrow={data.eyebrow}
                title={data.h1}
                subtitle={data.intro}
            >
                <div className="flex flex-wrap gap-3">
                    <a href={`tel:${ENQUIRY_PHONE_RAW}`} data-testid="prog-cta-call" className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-5 py-3 rounded-lg hover:bg-amber-400 transition-colors">
                        <Phone className="w-4 h-4" /> {ENQUIRY_PHONE}
                    </a>
                    <Link to="/register" data-testid="prog-cta-demo" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-white font-semibold px-5 py-3 rounded-lg hover:bg-white/15">
                        Book Free Demo <ArrowRight className="w-4 h-4" />
                    </Link>
                    <a href={`https://wa.me/${SOCIAL.whatsapp}?text=${encodeURIComponent(`Hi Anand Tutorials, I would like 1:1 online ${data.board.label} ${data.subject.label} tuition for ${data.city.name}.`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-5 py-3 rounded-lg hover:bg-[#1ebe5a]">
                        <MessageCircle className="w-4 h-4" /> WhatsApp
                    </a>
                </div>
            </PageHeader>

            {/* Trust strip */}
            <section className="bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                    {[
                        { i: Users, n: "50,000+", l: "Students taught" },
                        { i: Star, n: "4.9 / 5", l: `${data.city.name} parents` },
                        { i: Award, n: "98%", l: "Board pass rate" },
                        { i: Clock, n: "24 hrs", l: "Demo scheduled" },
                    ].map(({ i: I, n, l }) => (
                        <div key={l}>
                            <I className="w-6 h-6 text-[#1E3A8A] mx-auto mb-2" />
                            <div className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A]">{n}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{l}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Why us */}
            <section className="py-16 bg-white">
                <div className="max-w-5xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">
                        Why {data.city.name} families choose us
                    </div>
                    <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-8">
                        {data.city.name}&apos;s most trusted 1:1 online {data.board.label} {data.subject.label} tutors.
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {data.whyLocal.map((w, i) => (
                            <div key={i} className="flex items-start gap-3 p-5 rounded-2xl bg-[#F8FAFC] border border-slate-200">
                                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                                <div className="text-sm text-[#0F172A] leading-relaxed">{w}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Board / Subject / Delivery info */}
            <section className="py-16 bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">
                        {data.board.label} · {data.subject.label} · {data.city.name}
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8">
                        {data.subject.full} — 1:1 online for {data.board.full}
                    </h2>
                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            [Video, "Live 1:1 online", `Two-way HD video on Zoom or Google Meet. One student, one tutor, one personalised ${data.subject.label} plan.`],
                            [GraduationCap, "Board specialists", `Every ${data.board.label} ${data.subject.label} tutor has 5+ years of exam-board experience — knows the mark-scheme, paper structure and blueprint.`],
                            [ShieldCheck, "Free replacement", `Not satisfied? We rematch the tutor at no cost after the first 2 sessions — no questions asked.`],
                        ].map(([I, t, d]) => (
                            <div key={t} className="bg-white border border-slate-200 rounded-2xl p-6">
                                <div className="w-12 h-12 rounded-xl bg-blue-50 text-[#1E3A8A] flex items-center justify-center mb-3">
                                    <I className="w-6 h-6" />
                                </div>
                                <div className="font-heading font-semibold text-[#0F172A] mb-1">{t}</div>
                                <div className="text-sm text-slate-600 leading-relaxed">{d}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ + Registration */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-12">
                    <div className="lg:col-span-3">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">
                            {data.city.name} FAQs
                        </div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-8">
                            {data.board.label} {data.subject.label} in {data.city.name} — parents ask
                        </h2>
                        <div className="space-y-3">
                            {data.faqs.map(([q, a], i) => (
                                <details key={q} data-testid={`prog-faq-${i}`} className="group bg-[#F8FAFC] border border-slate-200 rounded-xl p-5 [&_summary::-webkit-details-marker]:hidden">
                                    <summary className="cursor-pointer font-heading font-semibold text-[#0F172A] flex items-center justify-between gap-3">
                                        {q}
                                        <span className="text-[#3B82F6] text-2xl group-open:rotate-45 transition-transform">+</span>
                                    </summary>
                                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{a}</p>
                                </details>
                            ))}
                        </div>

                        {/* Related programmatic links */}
                        <div className="mt-10 pt-8 border-t border-slate-100">
                            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400 mb-3">Related — same city, other subjects & boards</div>
                            <div className="flex flex-wrap gap-2">
                                <Link to={`/home-tuition-${data.citySlug}`} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-[#1E3A8A] hover:text-white text-slate-700 font-semibold transition-colors">
                                    All 1:1 online tuition in {data.city.name}
                                </Link>
                                {relatedSubjects.map((s) => {
                                    const sub = PROG_SUBJECTS[s];
                                    return (
                                        <Link key={s} to={`/${boardSlug}-${s}-tutor-${data.citySlug}`}
                                            className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-[#1E3A8A] hover:text-white text-slate-700 font-semibold transition-colors">
                                            {data.board.label} {sub.label} tutor in {data.city.name}
                                        </Link>
                                    );
                                })}
                                {relatedBoards.map((b) => {
                                    const brd = PROG_BOARDS[b];
                                    return (
                                        <Link key={b} to={`/${b}-${subjectSlug}-tutor-${data.citySlug}`}
                                            className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-[#1E3A8A] hover:text-white text-slate-700 font-semibold transition-colors">
                                            {brd.label} {data.subject.label} tutor in {data.city.name}
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-2" id="book-demo">
                        <RegistrationForm
                            title={`Book Free ${data.board.label} ${data.subject.label} Demo · ${data.city.name}`}
                            defaultCity={data.city.name}
                        />
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-16 bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white">
                <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">
                        Start your child&apos;s {data.board.label} {data.subject.label} journey in {data.city.name}.
                    </h2>
                    <p className="text-blue-100 max-w-2xl mx-auto mb-6">
                        Free 1:1 live online demo with a verified {data.board.label} {data.subject.label} tutor. No payment, no commitment.
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <a href={`tel:${ENQUIRY_PHONE_RAW}`} className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-6 py-3 rounded-lg hover:bg-amber-400">
                            <Phone className="w-4 h-4" /> Call {ENQUIRY_PHONE}
                        </a>
                        <Link to="/register" className="inline-flex items-center gap-2 bg-white text-[#1E3A8A] font-semibold px-6 py-3 rounded-lg hover:bg-slate-50">
                            Book Free Demo
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
